import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/auth';
import { toast } from '@/hooks/errors/use-toast';
import { CommunityAIService, AssistantConfig, CommunityAIResponse } from './services/community-ai.service';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import { logInfo, logError, LogCategory } from '@/utils/logger';

export interface TriggerCommunityAIParams {
  postContent: string;
  discussionId: string;
  category: string;
  openaiThreadId: string;
  originalUserId: string;
}

export interface CreateCommunityAIThreadParams {
  discussionId: string;
}

/**
 * Hook to create an OpenAI thread for a community discussion
 */
export const useCreateCommunityAIThread = () => {
  const { executeAIOperation } = useAIOperationWrapper();
  
  return useMutation({
    mutationFn: async ({ discussionId }: CreateCommunityAIThreadParams): Promise<string> => {
      logInfo('Creating community AI thread', { 
        category: LogCategory.USER_ACTION,
        data: { discussionId }
      });

      // Use AI operation wrapper for credit checking
      const openaiThreadId = await CommunityAIService.createCommunityAIThread(executeAIOperation);
      
      logInfo('Community AI thread created successfully', { 
        category: LogCategory.USER_ACTION,
        data: { discussionId, openaiThreadId }
      });

      return openaiThreadId;
    },
    onError: (error: any) => {
      logError('Failed to create community AI thread', {
        category: LogCategory.USER_ACTION,
        data: { error: error.message }
      });
      
      toast({
        title: "AI Setup Failed",
        description: "Failed to set up AI responses for this post",
        variant: "destructive",
      });
    },
  });
};

/**
 * Hook to trigger AI responses for a community discussion
 */
export const useTriggerCommunityAI = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (params: TriggerCommunityAIParams): Promise<CommunityAIResponse> => {
      logInfo('Triggering community AI responses', { 
        category: LogCategory.USER_ACTION,
        data: { 
          discussionId: params.discussionId,
          category: params.category,
          contentLength: params.postContent.length
        }
      });

      const result = await CommunityAIService.triggerCommunityAI(
        params.postContent,
        params.discussionId,
        params.category,
        params.openaiThreadId,
        params.originalUserId
      );

      logInfo('Community AI responses completed', { 
        category: LogCategory.USER_ACTION,
        data: { 
          discussionId: params.discussionId,
          success: result.success,
          responseCount: result.responses.length
        }
      });

      return result;
    },
    onSuccess: (result, variables) => {
      // Invalidate discussion thread data to show new AI responses
      queryClient.invalidateQueries({ 
        queryKey: ['discussion-thread-data', variables.discussionId, user?.id] 
      });
      
      // Also invalidate the legacy reply queries in case they're still being used elsewhere
      queryClient.invalidateQueries({ 
        queryKey: ['community-discussion-replies', variables.discussionId, user?.id] 
      });
      
      // Invalidate discussion to update reply count
      queryClient.invalidateQueries({ 
        queryKey: ['community-discussion', variables.discussionId, user?.id] 
      });
      
      // Invalidate discussions list to update reply counts
      queryClient.invalidateQueries({ 
        queryKey: ['community-discussions', user?.id] 
      });
      
      // Invalidate paginated discussions as well
      queryClient.invalidateQueries({ 
        queryKey: ['community-discussions-paginated'] 
      });

      const successCount = result.responses.filter(r => !r.error).length;
      const errorCount = result.responses.filter(r => r.error).length;

      if (successCount > 0) {
        toast({
          title: "AI Responses Generated",
          description: `${successCount} AI agent${successCount === 1 ? '' : 's'} responded to your post`,
          variant: "success",
        });
      }

      if (errorCount > 0) {
        toast({
          title: "Some AI Responses Failed",
          description: `${errorCount} AI agent${errorCount === 1 ? '' : 's'} failed to respond`,
          variant: "destructive",
        });
      }
    },
    onError: (error: any, variables) => {
      logError('Failed to trigger community AI responses', {
        category: LogCategory.USER_ACTION,
        data: { 
          error: error.message,
          discussionId: variables.discussionId,
          category: variables.category
        }
      });
      
      toast({
        title: "AI Response Failed",
        description: "Failed to generate AI responses for this post",
        variant: "destructive",
      });
    },
  });
};

/**
 * Hook to get AI agents configured for a specific category
 */
export const useGetCategoryAgents = (category: string) => {
  return useQuery({
    queryKey: ['community-category-agents', category],
    queryFn: async (): Promise<AssistantConfig[]> => {
      logInfo('Fetching AI agents for category', { 
        category: LogCategory.DATABASE,
        data: { category }
      });

      const agents = await CommunityAIService.getAgentsForCategory(category);
      
      logInfo('Retrieved AI agents for category', { 
        category: LogCategory.DATABASE,
        data: { category, agentCount: agents.length }
      });

      return agents;
    },
    enabled: !!category,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to check if AI agents are configured for a category
 */
export const useHasCategoryAgents = (category: string) => {
  const { data: agents = [], isLoading } = useGetCategoryAgents(category);
  
  return {
    hasAgents: agents.length > 0,
    agentCount: agents.length,
    agents,
    isLoading
  };
};

/**
 * Combined hook for the complete community AI workflow
 */
export const useCommunityAIWorkflow = () => {
  const createThread = useCreateCommunityAIThread();
  const triggerAI = useTriggerCommunityAI();
  
  const executeWorkflow = async (
    postContent: string,
    discussionId: string,
    category: string,
    originalUserId: string
  ) => {
    try {
      logInfo('Starting community AI workflow', { 
        category: LogCategory.USER_ACTION,
        data: { discussionId, category }
      });

      // Step 1: Create OpenAI thread
      const openaiThreadId = await createThread.mutateAsync({ discussionId });
      
      // Step 2: Trigger AI responses
      const result = await triggerAI.mutateAsync({
        postContent,
        discussionId,
        category,
        openaiThreadId,
        originalUserId
      });

      logInfo('Community AI workflow completed', { 
        category: LogCategory.USER_ACTION,
        data: { 
          discussionId,
          openaiThreadId,
          success: result.success,
          responseCount: result.responses.length
        }
      });

      return { openaiThreadId, aiResult: result };
    } catch (error: any) {
      logError('Community AI workflow failed', {
        category: LogCategory.USER_ACTION,
        data: { 
          error: error.message,
          discussionId,
          category
        }
      });
      throw error;
    }
  };

  return {
    executeWorkflow,
    isCreatingThread: createThread.isPending,
    isTriggeringAI: triggerAI.isPending,
    isLoading: createThread.isPending || triggerAI.isPending,
    error: createThread.error || triggerAI.error
  };
}; 